import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import marketingPagesData from "../content/marketingPages.json";
import { usePageMetadata } from "../hooks/usePageMetadata";
import {
  ArrowRightIcon,
  BookOpenIcon,
  GraduationCapIcon,
  ListIcon,
  LockIcon,
  ShieldCheckIcon,
} from "./SvgIcons";

type MarketingLocale = "en" | "zh";
type PrincipleIcon = "book" | "level" | "shield";

interface MarketingPageConfig {
  id: string;
  path: string;
  locale: MarketingLocale;
  title: string;
  description: string;
  nav: {
    textSimplifier: string;
    epubReader: string;
    chineseLearners: string;
    login: string;
    start: string;
    language: string;
  };
  heroTitle: string;
  heroBody: string;
  primaryCta: string;
  secondaryCta: string;
  demo: {
    originalLabel: string;
    adjustedLabel: string;
    originalParagraphs: string[];
    adjustedParagraphs: string[];
    originalHighlights: string[];
    adjustedHighlights: string[];
    definitionWord: string;
    definitionLabel: string;
    definitionBody: string;
  };
  principlesTitle: string;
  principles: Array<{
    icon: PrincipleIcon;
    title: string;
    body: string;
  }>;
  privacyTitle: string;
  privacyBody: string;
  finalTitle: string;
  finalCta: string;
}

const marketingPages = marketingPagesData as MarketingPageConfig[];

const localizedAlternates = [
  { language: "en", path: "/simplify-english-text" },
  { language: "zh-CN", path: "/zh/english-reading-tool" },
  { language: "x-default", path: "/simplify-english-text" },
];
const noAlternates: Array<{ language: string; path: string }> = [];

export const MARKETING_ROUTES = {
  textSimplifier: "/simplify-english-text",
  epubReader: "/english-epub-reader",
  chineseLearners: "/zh/english-reading-tool",
} as const;

export function getMarketingPage(
  pageId: MarketingPageConfig["id"],
): MarketingPageConfig {
  const page = marketingPages.find((candidate) => candidate.id === pageId);
  if (!page) throw new Error(`Unknown marketing page: ${pageId}`);
  return page;
}

const getSignupTarget = (): string => "/?mode=signup#auth-card";

function HighlightedText({
  text,
  highlights,
}: {
  text: string;
  highlights: string[];
}) {
  const expression = new RegExp(
    `(${[...highlights]
      .sort((left, right) => right.length - left.length)
      .map((value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|")})`,
    "gi",
  );

  return (
    <>
      {text.split(expression).map((part, index) =>
        highlights.some(
          (highlight) => highlight.toLowerCase() === part.toLowerCase(),
        ) ? (
          <mark
            key={`${part}-${index}`}
            className="rounded bg-violet-100 px-1 text-violet-800"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

const principleIcons: Record<
  PrincipleIcon,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  book: BookOpenIcon,
  level: ListIcon,
  shield: ShieldCheckIcon,
};

const MarketingHeader = ({ page }: { page: MarketingPageConfig }) => {
  const navigate = useNavigate();
  const navItems = [
    {
      label: page.nav.textSimplifier,
      to: MARKETING_ROUTES.textSimplifier,
    },
    { label: page.nav.epubReader, to: MARKETING_ROUTES.epubReader },
    {
      label: page.nav.chineseLearners,
      to: MARKETING_ROUTES.chineseLearners,
    },
  ];

  return (
    <header className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-8 gap-y-4 px-4 py-6 sm:px-6 lg:flex-nowrap lg:px-8">
      <Link
        to="/"
        className="flex shrink-0 items-center gap-3 rounded-lg font-bold text-gray-950 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
      >
        <img src="/logo192.png" alt="" className="h-10 w-10 rounded-xl" />
        <span className="text-xl">PageBunny</span>
      </Link>

      <nav
        aria-label={page.locale === "zh" ? "营销页面" : "Marketing pages"}
        className="order-3 flex w-full flex-wrap gap-x-4 gap-y-1 pb-1 text-xs font-medium text-slate-600 sm:text-sm lg:order-none lg:ml-auto lg:w-auto lg:flex-nowrap lg:gap-5 lg:pb-0"
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `whitespace-nowrap rounded-md px-1 py-2 transition hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                isActive ? "text-purple-700 underline underline-offset-8" : ""
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-3 lg:ml-0">
        <label className="sr-only" htmlFor="marketing-language">
          {page.nav.language}
        </label>
        <select
          id="marketing-language"
          value={page.locale}
          onChange={(event) =>
            navigate(
              event.target.value === "zh"
                ? MARKETING_ROUTES.chineseLearners
                : MARKETING_ROUTES.textSimplifier,
            )
          }
          className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="en">English</option>
          <option value="zh">中文</option>
        </select>
        <Link
          to={getSignupTarget()}
          className="hidden min-h-11 items-center justify-center rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:inline-flex"
        >
          {page.nav.start}
        </Link>
      </div>
    </header>
  );
};

const ReadingComparison = ({ page }: { page: MarketingPageConfig }) => (
  <div
    id="example"
    className="scroll-mt-6 overflow-hidden rounded-2xl border border-purple-100 bg-white shadow-xl shadow-purple-100/60"
  >
    <div className="grid lg:grid-cols-2">
      <section aria-labelledby={`${page.id}-original`}>
        <h2
          id={`${page.id}-original`}
          className="border-b border-slate-200 px-6 py-4 text-base font-bold text-slate-950"
        >
          {page.demo.originalLabel}
        </h2>
        <div className="space-y-5 px-6 py-5 text-[15px] leading-7 text-slate-700">
          {page.demo.originalParagraphs.map((paragraph) => (
            <p key={paragraph}>
              <HighlightedText
                text={paragraph}
                highlights={page.demo.originalHighlights}
              />
            </p>
          ))}
        </div>
      </section>

      <section
        aria-labelledby={`${page.id}-adjusted`}
        className="relative border-t border-slate-200 lg:border-l lg:border-t-0"
      >
        <h2
          id={`${page.id}-adjusted`}
          className="border-b border-slate-200 px-6 py-4 text-base font-bold text-purple-600"
        >
          {page.demo.adjustedLabel}
        </h2>
        <div className="space-y-5 px-6 py-5 pb-8 text-[15px] leading-7 text-slate-700 lg:pb-44">
          {page.demo.adjustedParagraphs.map((paragraph) => (
            <p key={paragraph}>
              <HighlightedText
                text={paragraph}
                highlights={page.demo.adjustedHighlights}
              />
            </p>
          ))}
        </div>
        <aside className="mx-6 mb-6 rounded-xl border border-purple-100 bg-white p-4 shadow-lg lg:absolute lg:bottom-5 lg:right-5 lg:mb-0 lg:max-w-[220px]">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-700">
            <BookOpenIcon className="h-4 w-4" />
            {page.demo.definitionWord}
          </div>
          <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-purple-500">
            {page.demo.definitionLabel}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {page.demo.definitionBody}
          </p>
        </aside>
      </section>
    </div>
  </div>
);

const MarketingFooter = ({ page }: { page: MarketingPageConfig }) => (
  <footer className="border-t border-purple-100/80 bg-white/40">
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 text-sm text-slate-600 sm:px-6 md:flex-row md:items-center lg:px-8">
      <Link to="/" className="flex items-center gap-2 font-bold text-gray-950">
        <img src="/logo192.png" alt="" className="h-8 w-8 rounded-lg" />
        PageBunny
      </Link>
      <div className="flex flex-wrap gap-x-6 gap-y-3 md:mx-auto">
        <Link to={MARKETING_ROUTES.textSimplifier}>
          {page.nav.textSimplifier}
        </Link>
        <Link to={MARKETING_ROUTES.epubReader}>{page.nav.epubReader}</Link>
        <Link to={MARKETING_ROUTES.chineseLearners}>
          {page.nav.chineseLearners}
        </Link>
      </div>
      <Link className="font-medium text-purple-700" to="/?mode=login#auth-card">
        {page.nav.login}
      </Link>
    </div>
  </footer>
);

const MarketingPage = ({ pageId }: { pageId: MarketingPageConfig["id"] }) => {
  const page = getMarketingPage(pageId);
  usePageMetadata({
    ...page,
    alternates: page.id === "epub-reader" ? noAlternates : localizedAlternates,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-gray-900">
      <MarketingHeader page={page} />
      <main>
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[minmax(0,0.74fr)_minmax(0,1.26fr)] lg:px-8 lg:pb-14 lg:pt-10">
          <div>
            <h1 className="max-w-xl text-4xl font-bold leading-[1.08] tracking-tight text-gray-950 sm:text-5xl">
              {page.heroTitle}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
              {page.heroBody}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link
                to={getSignupTarget()}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 font-semibold text-white shadow-sm transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                {page.primaryCta}
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <a
                href="#example"
                className="inline-flex min-h-12 items-center rounded-lg px-2 font-semibold text-purple-700 underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                {page.secondaryCta}
              </a>
            </div>
          </div>
          <ReadingComparison page={page} />
        </section>

        <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="mx-auto max-w-4xl text-center text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl">
            {page.principlesTitle}
          </h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {page.principles.map((principle) => {
              const PrincipleIcon = principleIcons[principle.icon];
              return (
                <div key={principle.title} className="flex gap-4">
                  <PrincipleIcon className="h-10 w-10 shrink-0 text-purple-600" />
                  <div>
                    <h3 className="font-semibold text-gray-950">
                      {principle.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {principle.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex gap-4 rounded-xl border border-purple-100 bg-purple-100/60 px-5 py-5 sm:items-center sm:px-8">
            <LockIcon className="mt-1 h-8 w-8 shrink-0 text-purple-600 sm:mt-0" />
            <div>
              <h2 className="font-semibold text-gray-950">
                {page.privacyTitle}
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                {page.privacyBody}
              </p>
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 pt-8 text-center sm:px-6">
          <GraduationCapIcon className="mx-auto h-10 w-10 text-purple-600" />
          <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-bold tracking-tight text-gray-950">
            {page.finalTitle}
          </h2>
          <Link
            to={getSignupTarget()}
            className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 font-semibold text-white shadow-sm transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            {page.finalCta}
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
        </section>
      </main>
      <MarketingFooter page={page} />
    </div>
  );
};

export default MarketingPage;
